import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useCanvas } from '../context/CanvasContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Sun, Grid, Eye, Edit3, Type, Link as LinkIcon, Plus, Minus, Undo } from 'lucide-react';
import './UIControls.css';

const UIControls = () => {
  const { theme, toggleTheme } = useTheme();
  const { mode, toggleMode, addBlock, snapToGrid, toggleSnapToGrid, zoomIn, zoomOut, zoom, undo, canUndo } = useCanvas();
  const [inputValue, setInputValue] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const isUrl = /^(http|https):\/\/[^ "]+$/.test(inputValue) || inputValue.includes('.');
    
    if (isUrl) {
       let url = inputValue;
       if (!/^https?:\/\//i.test(url)) {
           url = 'https://' + url;
       }
       addBlock('bookmark', { url, title: inputValue, description: 'No description' });
    } else {
       addBlock('text', { text: inputValue });
    }
    setInputValue('');
  };

  return (
    <>
      {/* Small Centered Logo */}
      <div
        style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          pointerEvents: 'none',
          opacity: 0.8
        }}
      >
        <img 
          src={theme === 'dark' ? '/white-logo.png' : '/black-logo.png'} 
          alt="Logo" 
          style={{ width: '40px', height: 'auto' }} 
        />
      </div>

    <div className="ui-controls-container">
      <AnimatePresence>
        {mode === 'edit' && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            style={{ pointerEvents: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}
          >
            <form 
                onSubmit={handleSubmit}
                className="ui-controls-form"
            >
                <div style={{ marginRight: '10px', color: 'gray' }}>
                    {inputValue.includes('.') ? <LinkIcon size={18} /> : <Type size={18} />}
                </div>
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Paste a link or type text..."
                    className="ui-controls-input"
                    autoFocus
                />
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div 
        className={`ui-controls-bar ${mode === 'edit' ? 'edit-mode' : 'view-mode'}`}
      >
        <button 
            className="icon-btn" 
            onClick={toggleMode} 
            title={mode === 'edit' ? "Switch to View Mode" : "Switch to Edit Mode"}
            style={{ 
                background: mode === 'view' ? 'var(--card-bg)' : 'transparent',
                padding: '10px',
                borderRadius: '50%',
                boxShadow: mode === 'view' ? '0 4px 20px var(--shadow-color)' : 'none'
            }}
        >
            {mode === 'edit' ? <Eye size={20} /> : <Edit3 size={20} />}
        </button>

        <AnimatePresence>
            {mode === 'edit' && (
                <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 'auto', opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    style={{ display: 'flex', gap: '12px', overflow: 'hidden' }}
                >
                    <div style={{ width: '1px', background: 'var(--border-color)', margin: '0 4px' }}></div>

                    <button 
                        className="icon-btn" 
                        onClick={undo} 
                        disabled={!canUndo}
                        title="Undo"
                        style={{ opacity: canUndo ? 1 : 0.3, cursor: canUndo ? 'pointer' : 'not-allowed' }}
                    >
                        <Undo size={20} />
                    </button>

                    <div style={{ width: '1px', background: 'var(--border-color)', margin: '0 4px' }}></div>

                    <button className="icon-btn" onClick={toggleTheme} title="Toggle Theme">
                        {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                    </button>
                    
                    <button 
                        className="icon-btn" 
                        onClick={toggleSnapToGrid} 
                        title="Snap to Grid"
                        style={{ color: snapToGrid ? 'var(--text-color)' : 'gray' }}
                    >
                        <Grid size={20} />
                    </button>

                    <div style={{ width: '1px', background: 'var(--border-color)', margin: '0 4px' }}></div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <button className="icon-btn" onClick={zoomOut} title="Zoom Out">
                            <Minus size={20} />
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px' }}>
                            <span style={{ fontSize: '12px', textAlign: 'center' }}>
                                {Math.round(zoom * 100)}%
                            </span>
                        </div>
                        <button className="icon-btn" onClick={zoomIn} title="Zoom In">
                            <Plus size={20} />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
};

export default UIControls;
